import React, { useState, useContext, useEffect } from 'react';
import { AuthContext, API } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, Building2 } from 'lucide-react';

export default function Login() {
  const { setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ email: '', password: '', remember_me: false });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem('remembered_email');
    // Sadece email'i geri yükle, şifreyi değil (tarayıcı auto-fill çakışmasını önler)
    if (savedEmail) {
      setLoginData(prev => ({ ...prev, email: savedEmail, remember_me: true }));
    }
    // Eski kaydedilmiş şifreleri temizle
    localStorage.removeItem('remembered_password');
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem('token', res.data.access_token);

      if (loginData.remember_me) {
        localStorage.setItem('remembered_email', loginData.email);
        // Şifreyi localStorage'da saklamıyoruz — tarayıcı şifre yöneticisiyle çakışıyor
      } else {
        localStorage.removeItem('remembered_email');
      }
      localStorage.removeItem('remembered_password');

      setUser(res.data.user);
      toast.success('Giriş başarılı!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('Şifre sıfırlama kodu email adresinize gönderildi!');
      setForgotStep(2);
    } catch {
      toast.error('Bir hata oluştu');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: forgotEmail,
        code: resetCode,
        new_password: newPassword,
      });
      toast.success('Şifreniz başarıyla güncellendi!');
      setShowForgotPassword(false);
      setForgotStep(1);
      setForgotEmail('');
      setResetCode('');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kod geçersiz veya süresi dolmuş');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - same as Pricing page */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 group"
          >
            <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-gray-900 text-lg group-hover:text-teal-700 transition-colors">
              Expogrid.io
            </span>
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/pricing')}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
            >
              Fiyatlar
            </button>
            <button
              onClick={() => navigate('/register')}
              className="text-sm bg-teal-600 hover:bg-teal-700 text-white font-semibold px-4 py-1.5 rounded-lg transition-colors"
              data-testid="header-register-btn"
            >
              Ücretsiz Dene
            </button>
          </div>
        </div>
      </header>

      {/* Login Form - centered */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Giriş Yap</h1>
            <p className="text-sm text-gray-500 mt-1">Hesabınıza erişin</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8" data-testid="login-card">
            <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="login-email" className="text-sm font-medium text-gray-700">
                  E-posta
                </Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="ornek@sirket.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                  className="h-11 border-gray-300 focus:border-teal-500"
                />
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <Label htmlFor="login-password" className="text-sm font-medium text-gray-700">
                  Şifre
                </Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    data-testid="login-password-input"
                    className="h-11 pr-10 border-gray-300 focus:border-teal-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="toggle-login-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Remember Me + Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remember-me"
                    checked={loginData.remember_me}
                    onCheckedChange={(checked) => setLoginData({ ...loginData, remember_me: checked })}
                    data-testid="remember-me-checkbox"
                  />
                  <Label htmlFor="remember-me" className="text-sm text-gray-600 cursor-pointer">
                    Beni Hatırla
                  </Label>
                </div>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-teal-600 hover:text-teal-800 font-medium"
                  data-testid="forgot-password-link"
                >
                  Şifremi Unuttum
                </button>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed mt-1"
                data-testid="login-submit-btn"
              >
                {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </button>
            </form>

            {/* Divider */}
            <div className="my-5 flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-100" />
              <span className="text-xs text-gray-400">veya</span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>

            {/* Register link */}
            <p className="text-center text-sm text-gray-500">
              Hesabınız yok mu?{' '}
              <button
                onClick={() => navigate('/register')}
                className="text-teal-600 hover:text-teal-800 font-semibold"
                data-testid="goto-register-link"
              >
                14 gün ücretsiz başlayın
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => { setShowForgotPassword(open); if (!open) { setForgotStep(1); setForgotEmail(''); setResetCode(''); setNewPassword(''); } }}>
        <DialogContent data-testid="forgot-password-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {forgotStep === 1 ? 'Şifremi Unuttum' : 'Yeni Şifre Belirle'}
            </DialogTitle>
            <DialogDescription>
              {forgotStep === 1
                ? 'Email adresinize şifre sıfırlama kodu göndereceğiz'
                : 'Email adresinize gönderilen 6 haneli kodu girin'}
            </DialogDescription>
          </DialogHeader>

          {forgotStep === 1 ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="forgot-email" className="text-sm font-medium text-gray-700">E-posta</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="ornek@sirket.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={forgotLoading}
                className="w-full h-10 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl text-sm transition-all disabled:opacity-60"
                data-testid="send-code-btn"
              >
                {forgotLoading ? 'Gönderiliyor...' : 'Kod Gönder'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="reset-code" className="text-sm font-medium text-gray-700">Sıfırlama Kodu</Label>
                <Input
                  id="reset-code"
                  type="text"
                  placeholder="6 haneli kod"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  required
                  maxLength={6}
                  data-testid="reset-code-input"
                  className="tracking-widest text-center text-lg font-mono"
                />
                <p className="text-xs text-gray-400">Email adresinizi kontrol edin</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-password" className="text-sm font-medium text-gray-700">Yeni Şifre</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    data-testid="new-password-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="toggle-new-password"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setForgotStep(1)}
                  className="flex-1 h-10 border border-gray-300 text-gray-700 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-all"
                  data-testid="back-btn"
                >
                  Geri
                </button>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="flex-1 h-10 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl text-sm transition-all disabled:opacity-60"
                  data-testid="reset-password-btn"
                >
                  {forgotLoading ? 'Kaydediliyor...' : 'Şifreyi Sıfırla'}
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
